import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faCircle,
  faPrint,
  faPlus,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import {
  equipmentIntakeService,
  type IntakeAccessoryInput,
  type IntakeOwnershipType,
  type AddressSelection,
} from '@/services/equipment-intake.service';
import { AddressOverview } from '@/features/equipment-intake/components/AddressOverview';
import { CustomerSummary } from '@/features/equipment-intake/components/CustomerSummary';
import {
  AddressPickModal,
  type PickTarget,
} from '@/features/equipment-intake/components/AddressPickModal';
import {
  AddressFormModal,
  type AddressFormMode,
} from '@/features/equipment-intake/components/AddressFormModal';
import {
  ContactFormModal,
  type ContactFormMode,
} from '@/features/equipment-intake/components/ContactFormModal';
import { AddContactPrompt } from '@/features/equipment-intake/components/AddContactPrompt';
import {
  applyReceivedFromChange,
  applyReturnChange,
} from '@/features/equipment-intake/components/received-from-wiring';
import { type Customer } from '@/services/customer.service';
import { equipmentService, type Equipment } from '@/services/equipment.service';
import { priorityService } from '@/services/priority.service';
import { warrantyTypeService } from '@/services/warranty-type.service';
import {
  WARRANTY_STATUS_LABELS,
  type InspectionWarrantyStatus,
} from '@/services/inspection.service';
import { EquipmentSearchCombobox } from '@/features/equipment-intake/EquipmentSearchCombobox';
import { CustomerSearchSelect } from '@/features/equipment-intake/CustomerSearchSelect';
import { CustomerFormModal } from '@/features/customers/components/CustomerFormModal';
import {
  AccessoryChecklist,
  type AccessoryRow,
} from '@/features/equipment-intake/components/AccessoryChecklist';
import {
  PhotoUploadSection,
  type PhotoEntry,
} from '@/features/equipment-intake/components/PhotoUploadSection';
import { EngineerSelect } from '@/features/equipment-intake/components/EngineerSelect';
import {
  ShipmentCard,
  type ShipmentState,
} from '@/features/equipment-intake/components/ShipmentCard';
import { EquipmentForm } from '@/features/equipment/components/EquipmentForm';
import {
  useIntakeCustomer,
  useIntakeCustomerContacts,
  useIntakeCustomerLocations,
  useIntakeEquipmentModel,
} from '@/features/equipment-intake/intake-queries';
import {
  ReceivedConditionChecklist,
  emptyReceivedCondition,
  toReceivedConditionPayload,
  type ReceivedConditionState,
} from '@/features/equipment-intake/components/ReceivedConditionChecklist';
import { OWNERSHIP_TYPES, SELECT_CLASS } from '@/features/equipment-intake/intake-form.constants';
import { PageHeader } from '@/components/shared/PageHeader';
import { Breadcrumb } from '@/components/shared/Breadcrumb';
import { FormField } from '@/components/shared/FormField';
import { Modal } from '@/components/shared/Modal';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { toast } from '@/lib/swal';
import { formatCustomerLabel } from '@/lib/customer-label';
import { useHasPermission } from '@/hooks/use-permissions';
import { OWNERSHIP_TYPE_LABELS } from '@/services/equipment-intake.service';
import { ROUTES } from '@/routes/routes';

const DRAFT_KEY = 'ops-intake-workspace-draft';

// ── Received Condition checklist — structured, purely observational ───────────

// ConditionFlagState, ReceivedConditionState, emptyReceivedCondition(),
// toReceivedConditionPayload() and ReceivedConditionChecklist moved to
// equipment-intake/components/ReceivedConditionChecklist.tsx so the intake
// EDIT screen captures the identical checklist. Moved verbatim — this page
// renders exactly as before.

// ── Receiving Checklist banner — UI only, never persisted ─────────────────────

function ReceivingChecklistBanner({ items }: { items: { label: string; done: boolean }[] }) {
  const doneCount = items.filter((i) => i.done).length;
  return (
    <div className="rounded-lg border border-border bg-muted/20 p-4">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Intake Completeness
        </span>
        <span className="text-xs text-muted-foreground">
          {doneCount}/{items.length} complete
        </span>
      </div>
      <div className="flex flex-wrap gap-2">
        {items.map((item) => (
          <span
            key={item.label}
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${
              item.done ? 'bg-green-500/10 text-green-700' : 'bg-muted text-muted-foreground'
            }`}
          >
            <FontAwesomeIcon icon={item.done ? faCheck : faCircle} className="h-2.5 w-2.5" />
            {item.label}
          </span>
        ))}
      </div>
    </div>
  );
}

function extractApiMessage(error: Error, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as { error?: { message?: string } } | undefined;
    if (body?.error?.message) return body.error.message;
  }
  return fallback;
}

// ── Draft persistence — LOCAL STORAGE ONLY (D-approved: no backend draft workflow) ──

interface DraftState {
  intakeDate: string;
  customerId: string;
  customerLocationId: string;
  customerContactId: string;
  equipmentId: string;
  equipmentLabel: string;
  ownershipTypeAtIntake: IntakeOwnershipType | '';
  complaintDescription: string;
  conditionOnReceipt: string;
  receivedCondition: ReceivedConditionState;
  accessoryRows: AccessoryRow[];
  remarks: string;
  priorityId: string;
  warrantyStatus: InspectionWarrantyStatus | '';
  warrantyTypeId: string;
  assignedEngineerId: string;
}

function loadDraft(): DraftState | null {
  try {
    const raw = window.localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as DraftState;
  } catch {
    return null;
  }
}

function saveDraft(draft: DraftState): void {
  window.localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
}

function clearDraft(): void {
  window.localStorage.removeItem(DRAFT_KEY);
}

const today = () => new Date().toISOString().slice(0, 10);

export function EquipmentIntakeWorkspacePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const can = useHasPermission();

  const existingDraft = useRef<DraftState | null>(loadDraft());
  const [draftRestored, setDraftRestored] = useState(!!existingDraft.current);
  const d = existingDraft.current;

  const [intakeDate, setIntakeDate] = useState(d?.intakeDate ?? today());
  const [customerId, setCustomerId] = useState(d?.customerId ?? '');
  const [customerLocationId, setCustomerLocationId] = useState(d?.customerLocationId ?? '');
  const [customerContactId, setCustomerContactId] = useState(d?.customerContactId ?? '');

  // ── Bill-To / Ship-To selections ────────────────────────────────────────────
  // Defaults from the three locked business cases: Bill-To -> customer default
  // (Infosys, Wipro), Ship-To -> the selected location (all three). Tata's
  // "bill to the branch" case is one click away.
  //
  // Deliberately NOT persisted in the local draft: a draft restored days later
  // must not silently re-apply a stale branch choice. The operator re-confirms.
  const [billTo, setBillTo] = useState<AddressSelection>({ source: 'CUSTOMER_DEFAULT' });
  const [shipTo, setShipTo] = useState<AddressSelection>({ source: 'CUSTOMER_DEFAULT' });

  // Search-first customer selection (Phase 1). The selected customer object is
  // held locally so its label renders without a preloaded list; a restored draft
  // carries only `customerId`, so it is resolved by id below.
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [, setIsNewCustomer] = useState(false);
  const [customerModalOpen, setCustomerModalOpen] = useState(false);
  const [locationModal, setLocationModal] = useState<AddressFormMode | null>(null);
  const [contactModal, setContactModal] = useState<ContactFormMode | null>(null);
  /** Location id awaiting the "add a contact?" answer, or null. */
  const [promptContactFor, setPromptContactFor] = useState<string | null>(null);
  /** Which compact row is being changed, if any. */
  const [picking, setPicking] = useState<PickTarget | null>(null);
  /** True once the operator moves Return off the site. */
  const [returnOverridden, setReturnOverridden] = useState(false);
  const [equipmentId, setEquipmentId] = useState(d?.equipmentId ?? '');
  const [equipmentLabel, setEquipmentLabel] = useState(d?.equipmentLabel ?? '');
  const [selectedEquipmentModelId, setSelectedEquipmentModelId] = useState<string | null>(null);
  const [ownershipTypeAtIntake, setOwnershipTypeAtIntake] = useState<IntakeOwnershipType | ''>(
    d?.ownershipTypeAtIntake ?? '',
  );
  const [complaintDescription, setComplaintDescription] = useState(d?.complaintDescription ?? '');
  const [conditionOnReceipt, setConditionOnReceipt] = useState(d?.conditionOnReceipt ?? '');
  const [receivedCondition, setReceivedCondition] = useState<ReceivedConditionState>(
    d?.receivedCondition ?? emptyReceivedCondition(),
  );
  const [accessoryRows, setAccessoryRows] = useState<AccessoryRow[]>(d?.accessoryRows ?? []);
  const [remarks, setRemarks] = useState(d?.remarks ?? '');
  const [priorityId, setPriorityId] = useState(d?.priorityId ?? '');
  const [warrantyStatus, setWarrantyStatus] = useState<InspectionWarrantyStatus | ''>(
    d?.warrantyStatus ?? '',
  );
  const [warrantyTypeId, setWarrantyTypeId] = useState(d?.warrantyTypeId ?? '');
  const [assignedEngineerId, setAssignedEngineerId] = useState(d?.assignedEngineerId ?? '');

  // Photos and shipment attachments are not draft-restorable (files aren't
  // serializable to localStorage) — every other field survives a reload.
  const [photoEntries, setPhotoEntries] = useState<PhotoEntry[]>([]);
  const [shipmentStates, setShipmentStates] = useState<ShipmentState[]>([]);
  const [registerEquipmentOpen, setRegisterEquipmentOpen] = useState(false);

  const pendingPhotoRef = useRef<PhotoEntry[]>([]);
  useEffect(() => {
    pendingPhotoRef.current = photoEntries;
  }, [photoEntries]);
  useEffect(() => {
    return () => {
      pendingPhotoRef.current.forEach((e) => URL.revokeObjectURL(e.previewUrl));
    };
  }, []);

  // Autosave draft on every change (debounced by React's batching — cheap, local only)
  useEffect(() => {
    saveDraft({
      intakeDate,
      customerId,
      customerLocationId,
      customerContactId,
      equipmentId,
      equipmentLabel,
      ownershipTypeAtIntake,
      complaintDescription,
      conditionOnReceipt,
      receivedCondition,
      accessoryRows,
      remarks,
      priorityId,
      warrantyStatus,
      warrantyTypeId,
      assignedEngineerId,
    });
  }, [
    intakeDate,
    customerId,
    customerLocationId,
    customerContactId,
    equipmentId,
    equipmentLabel,
    ownershipTypeAtIntake,
    complaintDescription,
    conditionOnReceipt,
    receivedCondition,
    accessoryRows,
    remarks,
    priorityId,
    warrantyStatus,
    warrantyTypeId,
    assignedEngineerId,
  ]);

  // A restored draft persists only `customerId`, so the full record is fetched
  // to label the picker. Skipped once a customer has been picked in-session.
  const { data: resolvedCustomer } = useIntakeCustomer(customerId, !selectedCustomer);
  const effectiveCustomer = selectedCustomer ?? resolvedCustomer ?? null;

  const { data: customerLocations } = useIntakeCustomerLocations(customerId);

  // Contacts belong to a location, so they are scoped to the selected location
  // and reload whenever the location changes.
  const { data: customerContacts } = useIntakeCustomerContacts(customerId, customerLocationId);
  const { data: priorities, isError: prioritiesError } = useQuery({
    queryKey: ['priorities-all'],
    queryFn: () => priorityService.listAll(),
  });
  const { data: warrantyTypes, isError: warrantyTypesError } = useQuery({
    queryKey: ['warranty-types-all'],
    queryFn: () => warrantyTypeService.listAll(),
  });
  const referenceDataError = prioritiesError || warrantyTypesError;
  const { data: modelData } = useIntakeEquipmentModel(selectedEquipmentModelId);

  const lastLoadedModelId = useRef<string | null>(null);
  useEffect(() => {
    if (!selectedEquipmentModelId) return;
    if (modelData && modelData.id !== lastLoadedModelId.current) {
      lastLoadedModelId.current = modelData.id;
      // Only auto-populate when the checklist is empty — avoids clobbering a
      // restored draft's accessory edits when re-selecting the same equipment.
      setAccessoryRows((prev) =>
        prev.length > 0
          ? prev
          : modelData.accessories.map((a) => ({
              accessoryId: a.id,
              accessoryName: a.name,
              status: 'RECEIVED' as const,
              notes: '',
            })),
      );
    }
  }, [selectedEquipmentModelId, modelData]);

  // Reset location/contact whenever the customer changes.
  const handleCustomerSelect = (customer: Customer | null) => {
    setCustomerId(customer?.id ?? '');
    setSelectedCustomer(customer);
    setIsNewCustomer(false);
    setCustomerLocationId('');
    setCustomerContactId('');
  };

  // A customer just created inline. New customers have no location or contact
  // yet, so open the location step immediately.
  const handleCustomerCreated = (customer: Customer) => {
    setCustomerId(customer.id);
    setSelectedCustomer(customer);
    setIsNewCustomer(true);
    setCustomerLocationId('');
    setCustomerContactId('');
    setCustomerModalOpen(false);
    // Customer → Location → Contact: the chain continues without being asked for.
    setLocationModal({ kind: 'create' });
  };

  // The locked chain, applied in one place. Return follows unless it was
  // deliberately overridden; the invoice is not passed in, so it cannot move.
  const handleLocationChange = (locationId: string) => {
    const next = applyReceivedFromChange(locationId, { returnTo: shipTo, returnOverridden });
    setCustomerLocationId(locationId);
    setCustomerContactId(next.contactId);
    setShipTo(next.returnTo);
  };

  const handleReturnChange = (next: AddressSelection) => {
    const r = applyReturnChange(next, customerLocationId);
    setShipTo(r.returnTo);
    setReturnOverridden(r.returnOverridden);
  };

  // A brand-new location has nobody at it, so the operator is offered the next
  // link in the chain rather than left staring at an empty contact field.
  const handleLocationCreated = (locationId: string) => {
    void queryClient.invalidateQueries({ queryKey: ['customer-locations', customerId] });
    handleLocationChange(locationId);
    setPromptContactFor(locationId);
  };

  const handleContactCreated = (contactId: string) => {
    void queryClient.invalidateQueries({
      queryKey: ['customer-location-contacts', customerId, customerLocationId],
    });
    setCustomerContactId(contactId);
  };

  const addPhotos = useCallback((files: File[]) => {
    const entries = files.map((file) => ({ file, previewUrl: URL.createObjectURL(file) }));
    setPhotoEntries((prev) => [...prev, ...entries]);
  }, []);
  const removePendingPhoto = useCallback(
    (i: number) => {
      URL.revokeObjectURL(photoEntries[i]!.previewUrl);
      setPhotoEntries((prev) => prev.filter((_, idx) => idx !== i));
    },
    [photoEntries],
  );

  const updateShipment = (idx: number, state: ShipmentState) =>
    setShipmentStates((prev) => prev.map((s, i) => (i === idx ? state : s)));
  const removeShipment = (idx: number) =>
    setShipmentStates((prev) => prev.filter((_, i) => i !== idx));
  const addShipment = () =>
    setShipmentStates((prev) => [
      ...prev,
      {
        shipmentType: 'COURIER',
        courierName: '',
        trackingNumber: '',
        dispatchDate: '',
        receivedDate: '',
        remarks: '',
        existingAttachments: [],
        removedAttachmentIndices: [],
        pendingAttachmentFiles: [],
      },
    ]);

  const registerEquipmentMutation = useMutation({
    mutationFn: equipmentService.create,
    onSuccess: (eq: Equipment) => {
      setEquipmentId(eq.id);
      setEquipmentLabel(`${eq.equipmentName} | ${eq.serialNumber} | ${eq.assetNumber}`);
      setSelectedEquipmentModelId(eq.equipmentModelId);
      setRegisterEquipmentOpen(false);
      toast.success('Equipment registered');
    },
    onError: (error: Error) =>
      toast.error(extractApiMessage(error, 'Failed to register equipment')),
  });

  const completeMutation = useMutation({
    mutationFn: async () => {
      const accessories: IntakeAccessoryInput[] = accessoryRows
        .filter((r) => r.accessoryName.trim())
        .map((r) => ({
          accessoryId: r.accessoryId ?? undefined,
          accessoryName: r.accessoryName.trim(),
          status: r.status,
          notes: r.notes.trim() || undefined,
        }));

      let intake = await equipmentIntakeService.create({
        intakeDate: new Date(intakeDate).toISOString(),
        customerId: customerId || undefined,
        customerLocationId: customerLocationId || undefined,
        customerContactId: customerContactId || undefined,
        equipmentId,
        ownershipTypeAtIntake: ownershipTypeAtIntake || undefined,
        complaintDescription: complaintDescription.trim(),
        conditionOnReceipt: conditionOnReceipt || undefined,
        receivedConditionJson: toReceivedConditionPayload(receivedCondition),
        remarks: remarks || undefined,
        accessories: accessories.length > 0 ? accessories : undefined,
        assignedEngineerId: assignedEngineerId || undefined,
        shipments:
          shipmentStates.length > 0
            ? shipmentStates.map((s) => ({
                shipmentType: s.shipmentType,
                courierName: s.courierName || undefined,
                trackingNumber: s.trackingNumber || undefined,
                dispatchDate: s.dispatchDate ? new Date(s.dispatchDate).toISOString() : undefined,
                receivedDate: s.receivedDate ? new Date(s.receivedDate).toISOString() : undefined,
                remarks: s.remarks || undefined,
              }))
            : undefined,
        // Selections only — the server resolves and freezes both addresses
        // inside the intake transaction. Omitted entirely when there is no
        // customer, since there would be nothing to resolve against.
        ...(customerId ? { billTo, shipTo } : {}),
        priorityId: priorityId || undefined,
        warrantyStatus: warrantyStatus || undefined,
        warrantyTypeId: warrantyTypeId || undefined,
      });

      for (const entry of photoEntries) {
        intake = await equipmentIntakeService.uploadPhoto(intake.id, entry.file);
      }

      for (let si = 0; si < shipmentStates.length; si++) {
        const shState = shipmentStates[si];
        if (!shState || shState.pendingAttachmentFiles.length === 0) continue;
        const shipment = intake.shipments[si];
        if (!shipment) continue;
        for (const file of shState.pendingAttachmentFiles) {
          intake = await equipmentIntakeService.uploadShipmentAttachment(
            intake.id,
            shipment.id,
            file,
          );
        }
      }

      return intake;
    },
    onSuccess: () => {
      toast.success('Inward completed');
      clearDraft();
      void queryClient.invalidateQueries({ queryKey: ['equipment-intake'] });
      navigate(ROUTES.EQUIPMENT_INTAKE);
    },
    onError: (error: Error) => toast.error(extractApiMessage(error, 'Failed to complete intake')),
  });

  const handleSaveDraft = () => {
    saveDraft({
      intakeDate,
      customerId,
      customerLocationId,
      customerContactId,
      equipmentId,
      equipmentLabel,
      ownershipTypeAtIntake,
      complaintDescription,
      conditionOnReceipt,
      receivedCondition,
      accessoryRows,
      remarks,
      priorityId,
      warrantyStatus,
      warrantyTypeId,
      assignedEngineerId,
    });
    toast.success('Draft saved locally');
  };

  const handleCancel = () => {
    navigate(ROUTES.EQUIPMENT_INTAKE);
  };

  const activeLocations = (customerLocations ?? []).filter((l) => !l.deletedAt);
  const activeContacts = (customerContacts ?? []).filter((c) => !c.deletedAt);
  const selectedLocation = activeLocations.find((l) => l.id === customerLocationId) ?? null;
  const selectedContact = activeContacts.find((c) => c.id === customerContactId) ?? null;

  const accessoriesReviewed =
    accessoryRows.length > 0 ||
    (!!selectedEquipmentModelId && !!modelData && modelData.accessories.length === 0);

  const checklistItems = [
    { label: 'Customer Selected', done: !!customerId },
    { label: 'Equipment Selected', done: !!equipmentId },
    { label: 'Complaint Entered', done: complaintDescription.trim().length > 0 },
    { label: 'Accessories Reviewed', done: accessoriesReviewed },
    { label: 'Photos Uploaded', done: photoEntries.length > 0 },
    { label: 'Engineer Assigned', done: !!assignedEngineerId },
  ];

  const estimatedNextStep = assignedEngineerId
    ? 'Engineer Inspection'
    : 'Waiting for Engineer Assignment';

  const canSubmit = !!equipmentId && complaintDescription.trim().length > 0;

  if (!can('intake:create')) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
        <FontAwesomeIcon icon={faTriangleExclamation} className="h-8 w-8 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">
          You do not have permission to create equipment intakes.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <Breadcrumb
        segments={[
          { label: 'Operations', to: ROUTES.DASHBOARD_OPERATIONS },
          { label: 'Equipment Inward Workspace' },
        ]}
      />
      <PageHeader
        title="Equipment Inward Workspace"
        description="Receive equipment, capture condition, and route it to an engineer"
      />

      {draftRestored && (
        <div className="flex items-center justify-between rounded-lg border border-primary/30 bg-primary/5 px-4 py-2 text-sm text-foreground">
          <span>Restored your unsaved draft from this device.</span>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => {
                clearDraft();
                window.location.reload();
              }}
              className="text-xs text-primary underline"
            >
              Discard draft
            </button>
            <button
              type="button"
              onClick={() => setDraftRestored(false)}
              className="text-xs text-muted-foreground underline"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {referenceDataError && (
        <div className="rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          Could not load reference data. Check your connection and refresh.
        </div>
      )}

      <ReceivingChecklistBanner items={checklistItems} />

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-2">
          {/* Customer & Location */}
          <section className="rounded-lg border border-border bg-card p-4 space-y-4">
            {/* No section heading here: the Customer field below already names
                this block, and having both read as two different things. */}
            <FormField label="Inward Date" htmlFor="ws-intake-date" required>
              <Input
                id="ws-intake-date"
                type="date"
                value={intakeDate}
                onChange={(e) => setIntakeDate(e.target.value)}
                required
              />
            </FormField>
            <FormField label="Customer" htmlFor="ws-customer">
              <CustomerSearchSelect
                selected={effectiveCustomer}
                onSelect={handleCustomerSelect}
                onCreateNew={() => setCustomerModalOpen(true)}
              />
            </FormField>
            {/* Read-only. Everything editable lives in the sections below, so
                this cannot become a second place that owns the address. The hint
                only applies while the field is empty, so the two never stack. */}
            {effectiveCustomer ? (
              <CustomerSummary customer={effectiveCustomer} />
            ) : (
              <p className="text-xs text-muted-foreground">
                Leave empty for company-owned equipment.
              </p>
            )}

            {customerId && (
              <AddressOverview
                customer={effectiveCustomer}
                locations={activeLocations}
                contacts={activeContacts}
                locationId={customerLocationId}
                contactId={customerContactId}
                billTo={billTo}
                shipTo={shipTo}
                onPickLocation={() => setPicking('location')}
                onAddLocation={() => setLocationModal({ kind: 'create' })}
                onEditLocation={() =>
                  selectedLocation &&
                  setLocationModal({ kind: 'edit-place', location: selectedLocation })
                }
                onPickContact={() => setPicking('contact')}
                onAddContact={() => setContactModal({ kind: 'create' })}
                onEditContact={() =>
                  selectedContact && setContactModal({ kind: 'edit', contact: selectedContact })
                }
                onPickReturn={() => setPicking('return')}
                onPickInvoice={() => setPicking('invoice')}
              />
            )}
          </section>

          {/* Equipment */}
          <section className="rounded-lg border border-border bg-card p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-foreground">Equipment</h2>
              {can('equipment:create') && (
                <button
                  type="button"
                  onClick={() => setRegisterEquipmentOpen(true)}
                  className="flex items-center gap-1.5 text-xs text-primary hover:underline"
                >
                  <FontAwesomeIcon icon={faPlus} className="h-3 w-3" />
                  Register Equipment
                </button>
              )}
            </div>
            <FormField label="Equipment" htmlFor="ws-equipment" required>
              <EquipmentSearchCombobox
                value={equipmentId}
                initialLabel={equipmentLabel || undefined}
                onSelect={(r) => {
                  setEquipmentId(r?.id ?? '');
                  setEquipmentLabel(
                    r ? `${r.equipmentName} | ${r.serialNumber} | ${r.assetNumber}` : '',
                  );
                  setSelectedEquipmentModelId(r?.equipmentModelId ?? null);
                }}
              />
            </FormField>
            <FormField label="Ownership Type" htmlFor="ws-ownership">
              <select
                id="ws-ownership"
                value={ownershipTypeAtIntake}
                onChange={(e) =>
                  setOwnershipTypeAtIntake(e.target.value as IntakeOwnershipType | '')
                }
                className={SELECT_CLASS}
              >
                <option value="">— Auto-detect from equipment —</option>
                {OWNERSHIP_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {OWNERSHIP_TYPE_LABELS[t]}
                  </option>
                ))}
              </select>
            </FormField>
          </section>

          {/* Complaint & Condition */}
          <section className="rounded-lg border border-border bg-card p-4 space-y-4">
            <h2 className="text-sm font-semibold text-foreground">Complaint &amp; Condition</h2>
            <FormField label="Complaint Description" htmlFor="ws-complaint" required>
              <Textarea
                id="ws-complaint"
                value={complaintDescription}
                onChange={(e) => setComplaintDescription(e.target.value)}
                placeholder="Describe the customer's complaint…"
                rows={3}
              />
            </FormField>
            <FormField label="Condition on Receipt (free text)" htmlFor="ws-condition">
              <Input
                id="ws-condition"
                value={conditionOnReceipt}
                onChange={(e) => setConditionOnReceipt(e.target.value)}
                placeholder="e.g. Minor scratches on casing"
              />
            </FormField>
          </section>

          {/* Received Condition checklist */}
          <section className="rounded-lg border border-border bg-card p-4 space-y-3">
            <h2 className="text-sm font-semibold text-foreground">Received Condition Checklist</h2>
            <p className="text-xs text-muted-foreground">
              Purely observational — recorded at receiving time, not used elsewhere in the workflow.
            </p>
            <ReceivedConditionChecklist
              value={receivedCondition}
              onChange={setReceivedCondition}
              disabled={false}
            />
          </section>

          {/* Accessories */}
          <section className="rounded-lg border border-border bg-card p-4 space-y-3">
            <h2 className="text-sm font-semibold text-foreground">
              Accessories {accessoryRows.length > 0 ? `(${accessoryRows.length})` : ''}
            </h2>
            <AccessoryChecklist rows={accessoryRows} onChange={setAccessoryRows} disabled={false} />
            {equipmentId && !selectedEquipmentModelId && (
              <p className="text-xs text-muted-foreground">
                This equipment has no model — add accessories manually.
              </p>
            )}
          </section>

          {/* Photos */}
          <section className="rounded-lg border border-border bg-card p-4 space-y-3">
            <h2 className="text-sm font-semibold text-foreground">Equipment Photos</h2>
            <PhotoUploadSection
              existingUrls={[]}
              removedIndices={[]}
              pendingEntries={photoEntries}
              onRemoveExisting={() => undefined}
              onAddFiles={addPhotos}
              onRemovePending={removePendingPhoto}
              disabled={false}
            />
          </section>

          {/* Shipments */}
          <section className="rounded-lg border border-border bg-card p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-foreground">
                Shipments {shipmentStates.length > 0 ? `(${shipmentStates.length})` : ''}
              </h2>
              <button
                type="button"
                onClick={addShipment}
                className="flex items-center gap-1.5 text-xs text-primary hover:underline"
              >
                <FontAwesomeIcon icon={faPlus} className="h-3 w-3" />
                Add Shipment
              </button>
            </div>
            {shipmentStates.length === 0 ? (
              <p className="text-xs text-muted-foreground">No shipments recorded.</p>
            ) : (
              <div className="space-y-3">
                {shipmentStates.map((state, i) => (
                  <ShipmentCard
                    key={i}
                    index={i}
                    state={state}
                    onChange={(s) => updateShipment(i, s)}
                    onRemove={() => removeShipment(i)}
                    disabled={false}
                  />
                ))}
              </div>
            )}
          </section>

          {/* Priority & Warranty — pass-through to the auto-created Inspection */}
          <section className="rounded-lg border border-border bg-card p-4 space-y-4">
            <h2 className="text-sm font-semibold text-foreground">Priority &amp; Warranty</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <FormField label="Priority" htmlFor="ws-priority">
                <select
                  id="ws-priority"
                  value={priorityId}
                  onChange={(e) => setPriorityId(e.target.value)}
                  className={SELECT_CLASS}
                >
                  <option value="">— None —</option>
                  {priorities?.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField label="Warranty Status" htmlFor="ws-warranty-status">
                <select
                  id="ws-warranty-status"
                  value={warrantyStatus}
                  onChange={(e) =>
                    setWarrantyStatus(e.target.value as InspectionWarrantyStatus | '')
                  }
                  className={SELECT_CLASS}
                >
                  <option value="">— None —</option>
                  {(Object.keys(WARRANTY_STATUS_LABELS) as InspectionWarrantyStatus[]).map((s) => (
                    <option key={s} value={s}>
                      {WARRANTY_STATUS_LABELS[s]}
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField label="Warranty Type" htmlFor="ws-warranty-type">
                <select
                  id="ws-warranty-type"
                  value={warrantyTypeId}
                  onChange={(e) => setWarrantyTypeId(e.target.value)}
                  className={SELECT_CLASS}
                >
                  <option value="">— None —</option>
                  {warrantyTypes?.map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.name}
                    </option>
                  ))}
                </select>
              </FormField>
            </div>
          </section>

          {/* Engineer Assignment */}
          <section className="rounded-lg border border-border bg-card p-4 space-y-3">
            <h2 className="text-sm font-semibold text-foreground">Engineer Assignment</h2>
            <FormField label="Assign Engineer" htmlFor="ws-engineer">
              <EngineerSelect
                value={assignedEngineerId}
                onChange={setAssignedEngineerId}
                disabled={false}
              />
            </FormField>
          </section>

          {/* Remarks */}
          <section className="rounded-lg border border-border bg-card p-4 space-y-3">
            <FormField label="Remarks" htmlFor="ws-remarks">
              <Textarea
                id="ws-remarks"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Any additional remarks…"
                rows={2}
              />
            </FormField>
          </section>
        </div>

        {/* Summary Panel — sticky, informational only */}
        <div className="lg:col-span-1">
          <div className="sticky top-4 space-y-4 rounded-lg border border-border bg-card p-4">
            <h2 className="text-sm font-semibold text-foreground">Summary</h2>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between gap-2">
                <dt className="text-muted-foreground">Customer</dt>
                <dd className="text-right text-foreground">
                  {effectiveCustomer ? formatCustomerLabel(effectiveCustomer) : 'Company Inventory'}
                </dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-muted-foreground">Equipment</dt>
                <dd className="text-right text-foreground">{equipmentLabel || '—'}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-muted-foreground">Accessories</dt>
                <dd className="text-foreground">{accessoryRows.length}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-muted-foreground">Photos</dt>
                <dd className="text-foreground">{photoEntries.length}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-muted-foreground">Shipments</dt>
                <dd className="text-foreground">{shipmentStates.length}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-muted-foreground">Priority</dt>
                <dd className="text-foreground">
                  {priorities?.find((p) => p.id === priorityId)?.name ?? '—'}
                </dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-muted-foreground">Warranty</dt>
                <dd className="text-foreground">
                  {warrantyStatus ? WARRANTY_STATUS_LABELS[warrantyStatus] : '—'}
                </dd>
              </div>
            </dl>
            <div className="rounded-lg bg-primary/5 px-3 py-2">
              <p className="text-xs text-muted-foreground">Estimated Next Step</p>
              <p className="text-sm font-medium text-foreground">{estimatedNextStep}</p>
            </div>

            <div className="space-y-2 pt-2">
              <Button
                onClick={() => completeMutation.mutate()}
                disabled={!canSubmit || completeMutation.isPending}
                className="w-full"
              >
                {completeMutation.isPending ? 'Completing…' : 'Complete Inward'}
              </Button>
              <Button variant="secondary" onClick={handleSaveDraft} className="w-full">
                Save Draft
              </Button>
              <Button variant="ghost" onClick={handleCancel} className="w-full">
                Cancel
              </Button>
              <Button variant="ghost" disabled title="Print not yet implemented" className="w-full">
                <FontAwesomeIcon icon={faPrint} className="mr-2 h-3.5 w-3.5" />
                Print Intake Receipt
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Defence in depth: hiding the trigger is not a control — the dialog
          itself must never open without the permission (C.1). */}
      <Modal
        open={registerEquipmentOpen && can('equipment:create')}
        onClose={() => setRegisterEquipmentOpen(false)}
        title="Register Equipment"
        size="lg"
      >
        <EquipmentForm
          initial={customerId ? { ownershipType: 'CUSTOMER', customerId } : undefined}
          onSubmit={(payload) => registerEquipmentMutation.mutate(payload)}
          onCancel={() => setRegisterEquipmentOpen(false)}
          saving={registerEquipmentMutation.isPending}
        />
      </Modal>

      {/* Inline creation (Phase 1) — customer, location, contact. */}
      <CustomerFormModal
        open={customerModalOpen}
        onClose={() => setCustomerModalOpen(false)}
        editTarget={null}
        onSaved={() => setCustomerModalOpen(false)}
        onCreated={(customer) => handleCustomerCreated(customer)}
      />
      {customerId && (
        <>
          {/* One location form for the whole screen — the same eight fields
              whether it is opened from here or from Bill-To / Ship-To. */}
          {locationModal && (
            <AddressFormModal
              open
              customerId={customerId}
              mode={locationModal}
              onClose={() => setLocationModal(null)}
              onSaved={(selection) => {
                if (locationModal.kind === 'create' && selection.locationId) {
                  handleLocationCreated(selection.locationId);
                }
              }}
            />
          )}

          {picking && (
            <AddressPickModal
              target={picking}
              customer={effectiveCustomer}
              locations={activeLocations}
              contacts={activeContacts}
              locationId={customerLocationId}
              contactId={customerContactId}
              billTo={billTo}
              shipTo={shipTo}
              onLocationChange={handleLocationChange}
              onContactChange={setCustomerContactId}
              onReturnChange={handleReturnChange}
              onInvoiceChange={setBillTo}
              onAddLocation={() => {
                setPicking(null);
                setLocationModal({ kind: 'create' });
              }}
              onAddContact={() => {
                setPicking(null);
                setContactModal({ kind: 'create' });
              }}
              onEditContact={(contact) => {
                setPicking(null);
                setContactModal({ kind: 'edit', contact });
              }}
              onClose={() => setPicking(null)}
            />
          )}

          <AddContactPrompt
            open={!!promptContactFor}
            locationName={
              activeLocations.find((l) => l.id === promptContactFor)?.name ?? 'this location'
            }
            onSkip={() => setPromptContactFor(null)}
            onAddContact={() => {
              setPromptContactFor(null);
              setContactModal({ kind: 'create' });
            }}
          />

          {contactModal && customerLocationId && (
            <ContactFormModal
              open
              customerId={customerId}
              locationId={customerLocationId}
              locationName={selectedLocation?.name ?? 'this location'}
              mode={contactModal}
              onClose={() => setContactModal(null)}
              onSaved={(contact) => handleContactCreated(contact.id)}
            />
          )}
        </>
      )}
    </div>
  );
}
